import {
  Button,
  Card,
  CardBody,
  CardHeader,
  type ColumnDef,
  DataTable,
  DatePicker,
  EmptyState,
  Field,
  FormGrid,
  FormGridItem,
  InlineMessage,
  Input,
  OptionSelect,
  Stack,
  Text,
  Textarea,
} from '@olytic/foundation';
import { useEffect, useState } from 'react';

import { formatCurrency, formatDate } from '../objects/format';
import { type BusinessCase, CASE_ROWS, type CaseRowsKey, TOKENS } from './businessCase';
import { NumberSetting } from './fields';
import type { CustomSection, ProposalLayout } from './proposal';
import { milestoneAmounts, type Milestone, type Phase, scheduleOf, type Terms, type Timeline } from '@quote-math/terms';
import type { ValueModel } from '@quote-math/valueModel';

const newId = () => Math.random().toString(36).slice(2, 10);

/** A text or date field saved on blur, or when a date is picked. */
function TextSetting({ label, value, onSave, date = false, multiline = false, help }: { label: string; value: string; onSave: (v: string) => void; date?: boolean; multiline?: boolean; help?: string | undefined }): JSX.Element {
  const [draft, setDraft] = useState(value);
  useEffect(() => setDraft(value), [value]);
  const commit = () => draft !== value && onSave(draft);
  return (
    <Field label={label} help={help}>
      {multiline ? (
        <Textarea rows={3} value={draft} onChange={(e) => setDraft(e.target.value)} onBlur={commit} />
      ) : date ? (
        <DatePicker value={draft} onChange={setDraft} onBlur={commit} onPick={(picked) => picked !== value && onSave(picked)} />
      ) : (
        <Input value={draft} onChange={(e) => setDraft(e.target.value)} onBlur={commit} />
      )}
    </Field>
  );
}

/** One custom section's title and text, saved on blur. */
function CustomEditor({ section, onSave, onRemove }: { section: CustomSection; onSave: (s: CustomSection) => void; onRemove: () => void }): JSX.Element {
  return (
    <div className="flex flex-col gap-3 border-b border-[var(--border)] pb-4 last:border-b-0 last:pb-0">
      <div className="flex items-end gap-3">
        <div className="min-w-0 flex-1">
          <TextSetting label="Section Title" value={section.title} onSave={(title) => onSave({ ...section, title })} />
        </div>
        <Button variant="subtle" onClick={onRemove}>
          Remove
        </Button>
      </div>
      <TextSetting label="Text" multiline value={section.body} onSave={(body) => onSave({ ...section, body })} />
    </div>
  );
}

const subhead = 'm-0 mt-2 text-[13px] font-strong text-[var(--fg)]';

/** A table in the business case: the ask's stages, risks, alternatives, IT answers. */
function CaseRows({ field, memo, onSave }: { field: CaseRowsKey; memo: BusinessCase; onSave: (m: BusinessCase) => void }): JSX.Element {
  type Row = Record<string, string> & { __i: string };
  const rows: Row[] = (memo[field] as unknown as Record<string, string>[]).map((r, i) => ({ ...r, __i: String(i) }));
  const set = (next: Record<string, string>[]) => onSave({ ...memo, [field]: next });
  const columns: ColumnDef<Row>[] = CASE_ROWS[field].map((c) => ({ key: c.key, label: c.label, width: 220, get: (r) => r[c.key] ?? '', editor: { kind: 'text', maxLength: 2000 } }));
  const blank = Object.fromEntries(CASE_ROWS[field].map((c) => [c.key, '']));
  return (
    <div className="flex flex-col items-start gap-2">
      {rows.length ? (
        <div className="w-full">
          <DataTable<Row>
            columns={columns}
            rows={rows}
            getRowId={(r) => r.__i}
            density="compact"
            onCellSave={(r, key, v) => set(rows.map(({ __i, ...x }) => (__i === r.__i ? { ...x, [key]: v ?? '' } : x)))}
            rowActions={[{ label: 'Remove', destructive: true, onSelect: (r) => set(rows.filter((x) => x.__i !== r.__i).map(({ __i: _, ...x }) => x)) }]}
          />
        </div>
      ) : null}
      <Button variant="neutral" size="sm" onClick={() => set([...rows.map(({ __i: _, ...x }) => x), blank])}>
        Add row
      </Button>
    </div>
  );
}

/** The business case: the investment memo's words, with live figures as tokens. */
function BusinessCaseCard({ memo, onSave }: { memo: BusinessCase; onSave: (m: BusinessCase) => void }): JSX.Element {
  const text = (key: keyof BusinessCase, label: string, help?: string) => (
    <TextSetting label={label} help={help} multiline value={memo[key] as string} onSave={(v) => onSave({ ...memo, [key]: v })} />
  );
  const line = (key: keyof BusinessCase, label: string) => <TextSetting label={label} value={memo[key] as string} onSave={(v) => onSave({ ...memo, [key]: v })} />;
  return (
    <Card>
      <CardHeader title="Business case" />
      <CardBody>
        <Stack>
          <Text tone="muted">
            The ask, problem, solution, risks, measures, alternatives, roles and IT notes. Any text can use{' '}
            {Object.keys(TOKENS)
              .map((t) => `{${t}}`)
              .join(', ')}{' '}
            for the live figure, and **bold**.
          </Text>
          <h3 className={subhead}>The ask</h3>
          {text('askLine', 'Headline', 'Leave blank for the default from the order.')}
          {text('recommendation', 'Recommendation')}
          <CaseRows field="stages" memo={memo} onSave={onSave} />
          <div className="grid gap-4 md:grid-cols-2">
            {line('owner', 'Owner at the customer')}
            {line('decideBy', 'Decision needed by')}
          </div>
          <h3 className={subhead}>The problem today</h3>
          {text('problem', "What's wrong today", 'The table under it comes from the Value tab.')}
          <h3 className={subhead}>What we&apos;ll build</h3>
          {text('solution', "What we'll build, one per line")}
          {text('changes', 'What changes day to day, one per line')}
          <h3 className={subhead}>Risks</h3>
          <CaseRows field="risks" memo={memo} onSave={onSave} />
          <h3 className={subhead}>Methodology</h3>
          {text('method', 'How we got these numbers, one per line', 'Shown above the assumptions table.')}
          <h3 className={subhead}>Measuring success</h3>
          {line('measureBy', 'Targets measured by')}
          {text('checkpoints', 'Checkpoints, one per line', 'The measures come from the Value tab.')}
          <h3 className={subhead}>Alternatives considered</h3>
          <CaseRows field="alternatives" memo={memo} onSave={onSave} />
          <h3 className={subhead}>Roles and commitments</h3>
          <div className="grid gap-4 md:grid-cols-2">
            {text('rolesOlytic', 'Olytic, one per line')}
            {text('rolesCustomer', 'Customer, one per line')}
          </div>
          <h3 className={subhead}>For IT</h3>
          {text('itIntro', 'Intro')}
          <CaseRows field="it" memo={memo} onSave={onSave} />
          <h3 className={subhead}>Data ownership, exit and accounting</h3>
          {text('exit', 'Data ownership and exit')}
          {text('accounting', 'Note under the financial detail')}
        </Stack>
      </CardBody>
    </Card>
  );
}

/**
 * Terms: the proposal's words. The business case, payment milestones and
 * terms, the timeline (which also sets when the ROI starts counting
 * benefits), who signs, and custom text sections.
 */
export function TermsTab({
  timeline,
  terms,
  oneTime,
  model,
  onSaveTimeline,
  onSaveTerms,
  onSaveModel,
  memo,
  onSaveMemo,
  layout,
  onSaveLayout,
}: {
  timeline: Timeline;
  terms: Terms;
  oneTime: number;
  model: ValueModel;
  onSaveTimeline: (t: Timeline) => void;
  onSaveTerms: (t: Terms) => void;
  onSaveModel: (m: ValueModel) => void;
  memo: BusinessCase;
  onSaveMemo: (m: BusinessCase) => void;
  layout: ProposalLayout;
  onSaveLayout: (l: ProposalLayout) => void;
}): JSX.Element {
  const schedule = scheduleOf(timeline);
  const milestones = milestoneAmounts(terms, oneTime);
  const setPhase = (id: string, patch: Partial<Phase>) => onSaveTimeline({ ...timeline, phases: timeline.phases.map((p) => (p.id === id ? { ...p, ...patch } : p)) });
  const setPayment = (patch: Partial<Terms['payment']>) => onSaveTerms({ ...terms, payment: { ...terms.payment, ...patch } });
  const setSignatures = (patch: Partial<Terms['signatures']>) => onSaveTerms({ ...terms, signatures: { ...terms.signatures, ...patch } });
  const setMilestone = (id: string, patch: Partial<Milestone>) => setPayment({ milestones: terms.payment.milestones.map((m) => (m.id === id ? { ...m, ...patch } : m)) });

  type PhaseRow = (typeof schedule.phases)[number];
  const phaseColumns: ColumnDef<PhaseRow>[] = [
    { key: 'name', label: 'Phase', width: 200, get: (p) => p.name, editor: { kind: 'text', maxLength: 200 } },
    { key: 'weeks', label: 'Weeks', align: 'right', nowrap: true, get: (p) => String(p.weeks), editor: { kind: 'number', min: 0, max: 104, step: 1 } },
    { key: 'from', label: 'From', nowrap: true, get: (p) => (p.from ? formatDate(p.from) : '—') },
    { key: 'to', label: 'To', nowrap: true, get: (p) => (p.to ? formatDate(p.to) : '—') },
    { key: 'description', label: 'What Happens', width: 420, get: (p) => p.description, editor: { kind: 'text', maxLength: 2000 } },
  ];
  type MilestoneRow = (typeof milestones.rows)[number];
  const milestoneColumns: ColumnDef<MilestoneRow>[] = [
    { key: 'name', label: 'Milestone', width: 280, get: (m) => m.name, editor: { kind: 'text', maxLength: 200 } },
    { key: 'pct', label: 'Share', align: 'right', nowrap: true, get: (m) => `${m.pct}%`, editor: { kind: 'number', min: 0, max: 100, getValue: (m) => String(m.pct) } },
    { key: 'amount', label: 'Amount', align: 'right', nowrap: true, render: (m) => formatCurrency(m.amount) },
  ];

  return (
    <Stack>
      <BusinessCaseCard memo={memo} onSave={onSaveMemo} />

      <Card>
        <CardHeader
          title="Payment terms"
          actions={
            <Button variant="neutral" size="sm" onClick={() => setPayment({ milestones: [...terms.payment.milestones, { id: newId(), name: 'New milestone', pct: 0 }] })}>
              Add milestone
            </Button>
          }
        />
        {milestones.balanced ? null : (
          <CardBody>
            <InlineMessage tone="warning">The milestones add to {milestones.totalPct}%, not 100%.</InlineMessage>
          </CardBody>
        )}
        <DataTable<MilestoneRow>
          columns={milestoneColumns}
          rows={milestones.rows}
          getRowId={(m) => m.id}
          density="compact"
          onCellSave={(m, key, v) => setMilestone(m.id, key === 'pct' ? { pct: Number(v) || 0 } : { name: v ?? '' })}
          rowActions={[{ label: 'Remove', destructive: true, onSelect: (m) => setPayment({ milestones: terms.payment.milestones.filter((x) => x.id !== m.id) }) }]}
        />
        <CardBody>
          <FormGrid>
            <FormGridItem>
              <NumberSetting label="Invoices Due (Days)" value={terms.payment.netDays} onSave={(v) => setPayment({ netDays: v ?? 30 })} />
            </FormGridItem>
            <FormGridItem>
              <Field label="Software Billed">
                <OptionSelect
                  options={[{ value: 'monthly', label: 'Monthly' }, { value: 'annual', label: 'Annually' }]}
                  value={terms.payment.billing}
                  onValueChange={(v) => setPayment({ billing: v as Terms['payment']['billing'] })}
                />
              </Field>
            </FormGridItem>
            <FormGridItem>
              <Field label="License Billed From">
                <OptionSelect
                  options={[{ value: 'launch', label: 'Launch' }, { value: 'signing', label: 'Signing' }]}
                  value={model.licenseFromSigning ? 'signing' : 'launch'}
                  onValueChange={(v) => onSaveModel({ ...model, licenseFromSigning: v === 'signing' })}
                />
              </Field>
            </FormGridItem>
            <FormGridItem span="full">
              <TextSetting label="Note" value={terms.payment.note} multiline onSave={(note) => setPayment({ note })} />
            </FormGridItem>
          </FormGrid>
        </CardBody>
      </Card>

      <Card>
        <CardHeader
          title="Timeline"
          actions={
            <Button
              variant="neutral"
              size="sm"
              onClick={() => onSaveTimeline({ ...timeline, phases: [...timeline.phases, { id: newId(), name: 'New phase', weeks: 2, description: '' }] })}
            >
              Add phase
            </Button>
          }
        />
        <CardBody>
          <div className="grid gap-4 md:grid-cols-2">
            <TextSetting label="Kickoff" date value={timeline.start} onSave={(start) => onSaveTimeline({ ...timeline, start })} />
            <Text tone="muted">
              {schedule.weeks ? `${schedule.weeks} weeks from kickoff` : 'No phases yet'}
              {schedule.goLive ? `, going live ${formatDate(schedule.goLive)}.` : '.'} Go-live ends the next-to-last phase; the ROI counts benefits from then.
            </Text>
          </div>
        </CardBody>
        {timeline.phases.length ? (
          <DataTable<PhaseRow>
            columns={phaseColumns}
            rows={schedule.phases}
            getRowId={(p) => p.id}
            density="compact"
            onCellSave={(p, key, v) => setPhase(p.id, key === 'weeks' ? { weeks: Number(v) || 0 } : { [key]: v ?? '' })}
            rowActions={[{ label: 'Remove', destructive: true, onSelect: (p) => onSaveTimeline({ ...timeline, phases: timeline.phases.filter((x) => x.id !== p.id) }) }]}
          />
        ) : (
          <CardBody>
            <EmptyState title="No phases yet" description="Discovery, build, testing and the weeks after go-live." />
          </CardBody>
        )}
      </Card>

      <Card>
        <CardHeader title="Signatures" />
        <CardBody>
          <FormGrid>
            <FormGridItem>
              <TextSetting label="Customer" value={terms.signatures.customerCompany} onSave={(customerCompany) => setSignatures({ customerCompany })} />
            </FormGridItem>
            <FormGridItem>
              <TextSetting label="Provider" value={terms.signatures.providerCompany} onSave={(providerCompany) => setSignatures({ providerCompany })} />
            </FormGridItem>
            <FormGridItem>
              <TextSetting label="Customer Signer" value={terms.signatures.customerName} onSave={(customerName) => setSignatures({ customerName })} />
            </FormGridItem>
            <FormGridItem>
              <TextSetting label="Olytic Signer" value={terms.signatures.providerName} onSave={(providerName) => setSignatures({ providerName })} />
            </FormGridItem>
            <FormGridItem>
              <TextSetting label="Customer Title" value={terms.signatures.customerTitle} onSave={(customerTitle) => setSignatures({ customerTitle })} />
            </FormGridItem>
            <FormGridItem>
              <TextSetting label="Olytic Title" value={terms.signatures.providerTitle} onSave={(providerTitle) => setSignatures({ providerTitle })} />
            </FormGridItem>
            <FormGridItem>
              <NumberSetting label="Valid for (Days)" value={terms.signatures.validDays} onSave={(v) => setSignatures({ validDays: v ?? 30 })} />
            </FormGridItem>
            <FormGridItem span="full">
              <TextSetting label="Acceptance Text" value={terms.signatures.text} multiline onSave={(text) => setSignatures({ text })} />
            </FormGridItem>
          </FormGrid>
        </CardBody>
      </Card>
      <Card>
        <CardHeader
          title="Custom sections"
          actions={
            <Button
              variant="neutral"
              size="sm"
              onClick={() => {
                const id = newId();
                onSaveLayout({ ...layout, sections: [...layout.sections, { key: `custom:${id}`, enabled: true }], custom: [...layout.custom, { id, title: 'New section', body: '' }] });
              }}
            >
              Add text section
            </Button>
          }
        />
        <CardBody>
          <Text tone="muted" className="mb-3">
            Text sections for the proposal, such as context or terms. A blank line starts a new paragraph; a line starting with a dash is a bullet. Turn them on,
            off or move them with Sections on the Proposal tab.
          </Text>
          {layout.custom.length ? (
            <Stack>
              {layout.custom.map((section) => (
                <CustomEditor
                  key={section.id}
                  section={section}
                  onSave={(next) => onSaveLayout({ ...layout, custom: layout.custom.map((c) => (c.id === next.id ? next : c)) })}
                  onRemove={() =>
                    onSaveLayout({ ...layout, sections: layout.sections.filter((x) => x.key !== `custom:${section.id}`), custom: layout.custom.filter((c) => c.id !== section.id) })
                  }
                />
              ))}
            </Stack>
          ) : (
            <Text tone="muted">No custom sections. Add one for context, terms or anything else, then place it with Sections.</Text>
          )}
        </CardBody>
      </Card>
    </Stack>
  );
}
